import React from "react";
import { ActivityMeasurement } from "../../types/postActivity";
import { formatMeasurementWithUnit } from "../../utils/unitUtils";

interface SessionActivitiesTableProps {
  measurements: ActivityMeasurement[];
  getActivityName: (measurement: ActivityMeasurement) => string;
  containerClassName?: string;
  hideTimeColumn?: boolean;
}

const SessionActivitiesTable: React.FC<SessionActivitiesTableProps> = ({
  measurements,
  getActivityName,
  containerClassName = "",
  hideTimeColumn = false,
}) => {
  if (!measurements || measurements.length === 0) {
    return null;
  }

  const groupedMeasurements = Object.entries(
    measurements.reduce(
      (groups, measurement) => {
        const groupKey = measurement.globalCategoryID
          ? `${measurement.ActivityTypeID}::${measurement.globalCategoryID}`
          : measurement.ActivityTypeID;
        if (!groups[groupKey]) {
          groups[groupKey] = [];
        }
        groups[groupKey].push(measurement);
        return groups;
      },
      {} as Record<string, ActivityMeasurement[]>,
    ),
  );

  type GroupEntry = [string, ActivityMeasurement[]];
  type Block = { supersetGroupId?: string; entries: GroupEntry[] };
  const blocks: Block[] = [];
  groupedMeasurements.forEach((entry) => {
    const supersetId = entry[1][0]?.supersetGroupId;
    const lastBlock = blocks[blocks.length - 1];
    if (supersetId && lastBlock?.supersetGroupId === supersetId) {
      lastBlock.entries.push(entry);
    } else {
      blocks.push({ supersetGroupId: supersetId, entries: [entry] });
    }
  });

  const renderActivityGroup = (
    groupKey: string,
    activityMeasurements: ActivityMeasurement[],
  ) => {
    const allVariableNames = new Set<string>();
    activityMeasurements.forEach((measurement) => {
      measurement.variables.forEach((variable) => {
        if (variable.hidden) return;
        if (variable.measurement !== null) {
          allVariableNames.add(variable.name);
        }
      });
    });
    const variableColumns = Array.from(allVariableNames);
    const groupRepresentative = activityMeasurements[0];

    return (
      <div key={groupKey} className="mb-4">
        <div className="font-grotesk font-bold text-base-content text-md mb-2">
          {getActivityName(groupRepresentative)}
        </div>

        <div className="overflow-x-auto scrollbar-hide">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-base-300">
                {variableColumns.map((variableName) => (
                  <th
                    key={variableName}
                    className="text-left py-2 px-2 font-medium text-base-content/80"
                  >
                    {variableName}
                  </th>
                ))}
                {!hideTimeColumn && (
                  <th className="text-left py-2 px-2 font-medium text-base-content/80">
                    Time
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              {activityMeasurements.map((measurement, measurementIndex) => (
                <tr
                  key={measurementIndex}
                  className="border-b border-base-200 hover:bg-base-200"
                >
                  {variableColumns.map((variableName) => {
                    const variable = measurement.variables.find(
                      (v) => v.name === variableName,
                    );
                    return (
                      <td
                        key={variableName}
                        className="py-1 px-2 text-base-content/80"
                      >
                        {formatMeasurementWithUnit(
                          variable?.measurement ?? null,
                          variable?.unit,
                        )}
                      </td>
                    );
                  })}
                  {!hideTimeColumn && (
                    <td className="py-1 px-2 text-base-content/60">
                      {measurement.timeLogged
                        ? new Date(measurement.timeLogged).toLocaleTimeString(
                            "en-US",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            },
                          )
                        : "-"}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className={containerClassName}>
      {blocks.map((block, blockIndex) => {
        const isSuperset = !!block.supersetGroupId && block.entries.length > 1;
        const content = block.entries.map(([groupKey, activityMeasurements]) =>
          renderActivityGroup(groupKey, activityMeasurements),
        );
        if (isSuperset) {
          return (
            <div
              key={`superset-${block.supersetGroupId}`}
              className="border-l-4 border-base-content/30 rounded-lg bg-base-content/5 pl-2 py-2 my-2"
            >
              <div className="px-2 pb-1">
                <span className="text-xs font-bold uppercase tracking-wider text-base-content/60">
                  Superset
                </span>
              </div>
              {content}
            </div>
          );
        }
        return (
          <React.Fragment key={`block-${blockIndex}`}>{content}</React.Fragment>
        );
      })}
    </div>
  );
};

export default SessionActivitiesTable;
